import React, { Dispatch, SetStateAction, useMemo, useState } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import LayersIcon from "@material-ui/icons/Layers";
import CheckIcon from "@material-ui/icons/Check";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import useTranslation from "../../../Hooks/useTypedTranslation";
import { MotionEventBarVisibility, MotionEnum } from "../../models/motionEvent";

interface Props {
  buttonStyles: string;
  motionEventBarVisibility: MotionEventBarVisibility;
  setMotionEventBarVisibility: Dispatch<SetStateAction<MotionEventBarVisibility>>;
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    menuItemContentWrapper: {
      display: "flex",
      alignItems: "center",
      padding: 10,
    },
    menuItemDot: {
      marginLeft: 10,
      height: 10,
      width: 10,
      borderRadius: 10,
    },
    menuItemText: {
      marginLeft: 10,
    },
  })
);

const MotionEventsButton = ({
  buttonStyles,
  motionEventBarVisibility,
  setMotionEventBarVisibility,
  containerRef,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { t } = useTranslation();
  const classes = useStyles();
  const PARENT_CONTAINER = containerRef.current;
  const handleToggleButtonClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleListItemClick = (motionType: MotionEnum) => {
    setMotionEventBarVisibility({
      ...motionEventBarVisibility,
      [motionType]: !motionEventBarVisibility[motionType],
    });
  };

  const ListItem = ({
    color,
    text,
    motionType,
    isSelected,
  }: {
    color: string;
    text: string;
    motionType: MotionEnum;
    isSelected: boolean;
  }) => (
    <MenuItem onClick={() => handleListItemClick(motionType)}>
      <div className={classes.menuItemContentWrapper}>
        <div style={{ visibility: isSelected ? "visible" : "hidden" }}>
          <CheckIcon />
        </div>
        <div className={classes.menuItemDot} style={{ backgroundColor: color }} />
        <div className={classes.menuItemText}>{text}</div>
      </div>
    </MenuItem>
  );

  const menuItems = useMemo(
    () => (
      <div>
        <ListItem
          color="#4f6"
          text={t("person")}
          motionType={MotionEnum.Person}
          isSelected={motionEventBarVisibility.person}
        />
        <ListItem
          color="#f46"
          text={t("vehicle")}
          motionType={MotionEnum.Vehicle}
          isSelected={motionEventBarVisibility.vehicle}
        />
        <ListItem
          color="#fff954"
          text={t("animal")}
          motionType={MotionEnum.Animal}
          isSelected={motionEventBarVisibility.animal}
        />
      </div>
    ),
    [motionEventBarVisibility]
  );

  return (
    <>
      <button
        id="motion-events-toggle-button"
        className={buttonStyles}
        type="button"
        onClick={handleToggleButtonClick}
      >
        <LayersIcon />
      </button>
      <Menu
        id="motion-events-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        container={PARENT_CONTAINER}
      >
        {menuItems}
      </Menu>
    </>
  );
};

export default MotionEventsButton;
